/**
 * The one sanctioned way to filter a `Select`'s options for a
 * `SelectSearchInput` (long lists — teams, the 31 competition stages).
 * Owns the query state AND the matching rule (`selectFilter.ts` —
 * case-insensitive, trimmed, accent-insensitive), so every call site
 * behaves identically instead of each rollout screen hand-rolling its own
 * slightly different search:
 *
 *   const { query, setQuery, visible } = useSelectFilter(teams);
 *   ...
 *   <SelectSearchInput value={query} onChange={(e) => setQuery(e.target.value)} />
 *   {visible.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
 *
 * Matches against both `label` and `value` (a filter typed against a code
 * like "GK" should still find "Goalkeeper" either way).
 */

import { useMemo, useState } from "react";
import { matchesQuery } from "./selectFilter";

export interface SelectFilterableOption {
  value: string;
  label: string;
}

export function useSelectFilter<T extends SelectFilterableOption>(
  options: T[],
) {
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    if (!query.trim()) return options;
    return options.filter(
      (option) =>
        matchesQuery(option.label, query) || matchesQuery(option.value, query),
    );
  }, [options, query]);

  return { query, setQuery, visible };
}
